import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { fetchHotLeads } from '@/lib/admin/intents';
import { notifyUser, grantTrialExtension } from '@/lib/admin/users';
import { FireIcon } from '@heroicons/react/24/outline';

export default function HotLeadsPage() {
  const { t } = useTranslation();
  const { data } = useQuery({ queryKey: ['admin', 'hot-leads'], queryFn: fetchHotLeads });
  const [busy, setBusy] = useState<string | null>(null);

  const notify = useMutation({
    mutationFn: (userId: string) => notifyUser(userId, {
      title: t('admin.hotLeads.defaultTitle'),
      message: t('admin.hotLeads.defaultMessage'),
      channel: 'EMAIL',
    }),
    onSuccess: () => toast.success(t('admin.toasts.notificationSent')),
    onError: () => toast.error(t('common.error')),
    onSettled: () => setBusy(null),
  });

  const trial = useMutation({
    mutationFn: (userId: string) => grantTrialExtension(userId, 14),
    onSuccess: () => toast.success(t('admin.toasts.trialGranted')),
    onError: () => toast.error(t('common.error')),
    onSettled: () => setBusy(null),
  });

  const leads = data ?? [];

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
        <FireIcon className="h-6 w-6 text-orange-500" /> {t('admin.nav.hotLeads')}
      </h1>

      {leads.length === 0 && (
        <p className="text-sm text-gray-500">{t('common.noResults')}</p>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {leads.map((l) => {
          const fullName = `${l.user.firstName} ${l.user.lastName}`.trim() || l.user.email;
          return (
            <div key={l.user.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between">
                <Link to={`/admin/users/${l.user.id}`} className="text-base font-semibold text-gray-900 hover:underline">
                  {fullName}
                </Link>
                <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                  {Math.round(l.intentScore)}
                </span>
              </div>
              <p className="text-xs text-gray-500">{l.user.email}</p>

              <div className="mt-3 flex flex-wrap gap-1">
                {l.topIntents.slice(0, 3).map((it) => (
                  <span key={it.type} className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                    {t(`intents.${it.type}`, { defaultValue: it.type })}
                  </span>
                ))}
              </div>

              {l.recommendedAction && (
                <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  💡 {t(l.recommendedAction)}
                </div>
              )}

              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  disabled={busy === l.user.id}
                  onClick={() => { setBusy(l.user.id); notify.mutate(l.user.id); }}
                  className="flex-1 rounded-lg bg-primary-700 px-3 py-2 text-xs font-medium text-white hover:bg-primary-800 disabled:opacity-50"
                >
                  {t('admin.actions.notify')}
                </button>
                <button
                  type="button"
                  disabled={busy === l.user.id}
                  onClick={() => { setBusy(l.user.id); trial.mutate(l.user.id); }}
                  className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {t('admin.actions.grantTrial')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
